/**
	* App View
	* Handles: root component, popups
	* @module App
*/

/// Imports

// React & JQuery
import React, {useState, useEffect} from 'react';
import $ from 'jquery';

// OpenLayers
import OlMap from 'ol/Map';
import DragPan from 'ol/interaction/DragPan';
import ZoomCtl from 'ol/control/Zoom';
import ZoomSliderCtl from 'ol/control/ZoomSlider';
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom';
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom';

// Models
import Vector2 from 'lib/classes/vector2';
import MapTypes from 'lib/interfaces/maptypes';
import { Popup, drawPopups } from 'lib/classes/popup';
import MapToolMode from 'lib/enums/maptoolmode';

// Views
import MapContainer from 'views/MapContainer';
import Tools from 'views/Tools';
import Attributions from 'views/Attributions';
import OverviewMap from 'views/OverviewMap';

// UI
import EntryPopup from 'views/popups/Entry';
import Guide from 'views/popups/Guide';
import DisclaimerPopup from 'views/popups/Disclaimer';

// Tools
import { getWindowDimensions, parseURLs } from 'lib/utilities/misc';
import { getMap } from 'config/map';
import { getToolModeByName, getToolEnabled } from 'lib/utilities/toolmodes';
import { fetchJson } from 'lib/utilities/network';

// Lang
import { getLocaleString } from 'lib/localization/vendor';

// Styles
import 'styles/css/App.scss';

/// Variable declarations
var initialized:boolean = false;
var initialMapTransformTimer:any;

function App() {
	// State hooks
	const [popupsOpen, setPopupsOpen] = useState<Popup[]>([]);
	const [dimensions, setDimensions] = useState(getWindowDimensions());
	const [olMap, setOlMap] = useState<OlMap>();
	const [overviewMap, setOverviewMap] = useState<OlMap>();
	const [viewerReady, setViewerReady] = useState<boolean>(false);
	const [mapConfig, setMapConfig] = useState<any>({});
	const [accessStyling, setAccessStyling] = useState<boolean>(_config.accessStylingEnabled);
	
	// Get styled tooltip from UI manager
	const StyledTooltip = _ui.tooltip;
	
	// Called when the map instance changes
	useEffect(() => {
		if(olMap) {
			setTimeout(() => checkForGuide(), 1000);
			setTimeout(() => checkForDisclaimer(), 100);
		}
	}, [olMap]);
	
	// Called when map control tool toggled
	useEffect(() => {
		if(!olMap)
			return;
			
		const mapControlEnabled:boolean = getToolEnabled(MapToolMode.MapControl, mapConfig);
		const dragPan:any = olMap.getInteractions().getArray().find(interaction => interaction instanceof DragPan);
		const mwZoom:any = olMap.getInteractions().getArray().find(interaction => interaction instanceof MouseWheelZoom);
		const dcZoom:any = olMap.getInteractions().getArray().find(interaction => interaction instanceof DoubleClickZoom);
		const zoomCtl:any = olMap.getControls().getArray().find(ctl => ctl instanceof ZoomCtl);
		
		if(mapControlEnabled) {
			if(!dragPan)
				olMap.getInteractions().extend([new DragPan()]);
			
			if(!mwZoom)
				olMap.getInteractions().extend([new MouseWheelZoom()]);
				
			if(!dcZoom)
				olMap.getInteractions().extend([new DoubleClickZoom()]);
			
			//if(!zoomCtl)
			//	olMap.getControls().extend([new ZoomCtl()]);
		}else{
			if(dragPan)
				olMap.getInteractions().remove(dragPan);
			
			if(mwZoom)
				olMap.getInteractions().remove(mwZoom);
			
			if(dcZoom)
				olMap.getInteractions().remove(dcZoom);
				
			//if(zoomCtl)
			//	olMap.getControls().remove(zoomCtl);
		}
		
	}, [olMap, getToolEnabled(MapToolMode.MapControl, mapConfig)]);
	
	/**
		Initializes the module
	*/
	const initialize = () => {
		setViewerReady(false);
		
		window.addEventListener('resize', onWindowResize);
		onWindowResize(); // define _dimensions
		
		// Global popup event handled only by this view
		_events.on('show-popups', (popups:Popup[]) => {
			setPopupsOpen([...popups]);
		});
		
		// Mouse move UI handler
		$(document).mousemove(function(e) {
			const pos:Vector2 = new Vector2(e.pageX, e.pageY);
			
			_ui.mousePosition = pos;
			_events.invoke('mouse-move', pos);
		});
		
		// Video wrappers
		_events.on('resize-video-wrappers', () => {
			$('.video-wrapper').each(function() {
				var $this = $(this);
				var $parent = $this.parent();
				var parentWidth = $parent.width();
				
				$this.css('height', parentWidth * 0.5625); // Set the height to 56.25% of the width (16:9 aspect ratio)
			});
		});
		$(document).ready(function() {
			const call = () => _events.invoke('resize-video-wrappers');
			call(); // Call the function on page load
			$(window).resize(call); // Bind the function to the resize event
		});
		
		/**
			* Handle window events from parent
			* 
			* @info Sent from admin console to OMV
		*/
		const onEventReceived = (event:MessageEvent) => {
			const payload:any = event?.data?.payload;
			
			if(!payload)
				return;
			
			const name:string = payload.event;
			const callbackId:string = payload.callbackId;
			
			if(callbackId !== _config.parentWindowCallbackId)
				return;
			
			console.log('[OMV] Event received', payload);
			
			_events.invoke(`window:${name}`, event);
		}
		window.addEventListener('message', onEventReceived);
		
		/**
			* Parent frame invoke event
			* 
			* @info Invokes an event on the parent window
		*/
		_events.on('parent-invoke', (data:any) => {
			window.parent.postMessage({ type: 'omvevent', payload: {...data, callbackId: _config.parentWindowCallbackId} }, '*');
		})
		
		/**
			* Map viewer ready event handler
			* 
			* @param clientId - id of opus client
			* @param mapId - id of opus map
		*/
		_events.on('map-viewer-ready', async () => {
			const mapId:number = _config.mapId;
			const clientId:number = _config.clientId;
			
			// Get map
			const maps:MapTypes = await getMap(mapId, clientId);
			const map:OlMap = maps.map;
			const overviewMap:OlMap = maps.overviewMap;
			
			if(!map) {
				/*_ui.showError(`Could not load map id ${mapId}. Continue to try a different one.`, '', () => {
					EntryPopup.set('mapId', mapId);
					EntryPopup.set('clientId', clientId);
					_ui.showPopup(EntryPopup);
					return false;
				});*/
				_ui.showError(`Could not load map id ${mapId} with client id ${clientId}.`);
				return;
			}
			
			_ui.closePopup();
			startNewMap(map, overviewMap);
		});
		
		/**
			* Map viewer new config ready
			* 
			* @param {any} mapConfig - New map config object
			* @remarks
			* Sets the new map config to the map as a property.
			* Then updates the state hook with map config to reload DOM.
		*/
		_events.on('new-map-config', (map:OlMap, mapConfig:any) => {
			map.set('opus-config', mapConfig);
			
			setMapConfig(mapConfig);
		});
		
		/**
			* Accessibility toggle event
			* 
			* @param {boolean} enabled - Should accessibility styling be enabled
		*/
		_events.on('toggle-access-styling', (enabled:boolean) => {
			_config.accessStylingEnabled = enabled;
			setAccessStyling(enabled);
			
			if(enabled) {
				localStorage.setItem('omv-access', 'true');
			}else{
				localStorage.removeItem('omv-access');
			}
		});
		
		// Adjust resolution for higher res displays
		adjustResolution();
		
		// Load the data attributes
		loadDataAttributes();
		
		initialized = true;
	}
	
	const adjustResolution = () => {
		/*
			Adjust for higher resolution displays
		*/
		/*const res:number = dimensions.width * dimensions.height;
		const resLimit:number = 1336320;
		
		let zoom:number = res / resLimit * 0.5;
		
		if(zoom > 2) zoom = 2;
		
		$('body').css('zoom', zoom < 1 ? 1 : zoom);*/
		
		const res:number = dimensions.width * dimensions.height;
		const resLimit:number = 1336320;
		
		/*if(res > resLimit)
			_ui.showNotice(getLocaleString('reswarning'));*/
	}
	
	/**
		* Start new map
		* 
		* @param {OlMap} map - OpenLayers map to use
	*/
	const startNewMap = (map:OlMap, overviewMap:OlMap) => {
		// For testing
		(window as any).olMap = map;
		
		// Reset the URL hash
		history.replaceState(undefined, undefined, '');
		
		// Reset the ol map container on the dom
		$('#olmapcontainer').html('');
		
		// Assign needed global properties to map
		map.set('opus-layer-groups', []);
		
		// Call global event saying a new map has been started
		_events.invoke('new-map-started', map);
		
		// When the map is moved/zoomed
		map.on('moveend', () => {
			const center = map.getView().getCenter();
			const zoom = map.getView().getZoom();
			
			_events.invoke('parent-invoke', {
				event: 'map-position', 
				center, zoom
			});
		});
		
		// Hash injection
		_events.on('window:inject-hash', (event:MessageEvent) => {
			const payload:any = event.data.payload;
			const hash:string = payload.hash;
			
			clearTimeout(initialMapTransformTimer);
			const win:any = window;
			win.injectHash(hash);
		});
		
		// Set hash (For external use)
		_events.on('window:set-hash', (event:MessageEvent) => {
			const payload:any = event.data.payload;
			const hash:string = payload.hash;
			
			clearTimeout(initialMapTransformTimer);
			window.location.hash = hash;
		});
		
		// Set hash (For internal use)
		_events.on('set-hash', (hash:string) => {
			clearTimeout(initialMapTransformTimer);
			window.location.hash = hash;
		});
		
		// Get hash
		_events.on('window:get-hash', () => {
			const hash:string = window.location.hash;
			_events.invoke('parent-invoke', {event: 'omv-hash', hash});
		})
		
		// If there is an initial center/zoom set here
		initialMapTransformTimer = setTimeout(() => {
			const initialCenter:string = String(_config.initialCenter || '');
			const initialZoom:number = Number(_config.initialZoom);
			
			if(initialCenter) {
				const coords:number[] = initialCenter.split(',').map(Number);
				map.getView().setCenter(coords);
			}
			if(initialZoom) {
				map.getView().setZoom(initialZoom);
			}
		}, 100);
		
		// Set up the map view if a map view name is provided
		if(_config.mapViewName) {
			setupCustomMapView(map, _config.mapViewName);
		}
		
		// Set the new map in the component state and re-render
		setOlMap(map);
		setOverviewMap(overviewMap);
		setMapConfig(map.get('opus-config'));
		setViewerReady(true);
		readyForWindowEvent();
	}
	
	/**
		Sets up custom map view
	*/
	const setupCustomMapView = async (map:OlMap, viewName:string) => {
		const mapConfig:any = map.get('opus-config');
		try {
			const res:any = await fetchJson(`${_config.middleAPI}/api/v1/map-views/${mapConfig.id}?name=${viewName}`);
			const mapView:any = res?.mapView;
			if(!mapView || !mapView.hash || typeof mapView.hash !== 'string' || !mapView.hash.startsWith('#')) {
				console.warn('Invalid map view for map id', mapConfig.id, 'and view name', viewName, mapView);
				return;
			}
			
			_events.invoke('set-hash', mapView.hash);
		}catch(e:any) {
			console.error('Could not setup custom map view.', e);
		}
	}
	
	/**
		* Check if the guide needs showing and if it does, show it
	*/
	const checkForGuide = () => {
		if(!getToolEnabled(MapToolMode.Tutorial, mapConfig))
			return;
		
		const guideShown:boolean = !!localStorage.getItem('omv-guide-shown');
		
		if(guideShown) return;
		
		Guide.set('map', olMap);
		
		_ui.showPopup(Guide);
	}
	
	/**
		* Check if the disclaimer needs showing and if it does, show it
	*/
	const checkForDisclaimer = () => {
		if(!mapConfig?.disclaimer)
			return;
		
		DisclaimerPopup.set('map-config', mapConfig);
		
		_ui.showPopup(DisclaimerPopup);
	}
	
	/**
		Ready for the parent window event to declare OMV is ready
	*/
	const readyForWindowEvent = () => {
		if(_config.parentWindowCallbacks)
			_events.invoke('parent-invoke', {event: 'omv-ready'});
	}
	
	const loadDataAttributes = () => {
		_ui.showLoading();
		
		const element = $('#opusmap');
		
		_config.assetURL = element.data('assets') ?? '';
		_config.mapId = element.data('map-id') ?? 0;
		_config.clientId = element.data('client-id') ?? 0;
		_config.storageId = element.data('storage-id') ?? 0;
		_config.UUID = element.data('site-id') ?? _config.UUID; // site id can be overridden by attribute
		_config.showLayers = element.data('show-layers') ?? _config.showLayers;
		_config.showAllLayersOnStart = element.data('show-all-layers-start') ?? _config.showAllLayersOnStart;
		_config.defaultLayers = element.data('default-layers')?.split(',');
		_config.pinDropEnabled = element.data('pin-drop') ?? _config.pinDropEnabled;
		_config.queryLayersEnabled = element.data('query-layers') ?? _config.queryLayersEnabled;
		_config.toolsEnabledOverride = element.data('tools-enabled')?.split(',') ?? _config.toolsEnabledOverride;
		_config.toolsDisabledOverride = element.data('tools-disabled')?.split(',') ?? _config.toolsDisabledOverride;
		_config.drawToolsOverride = element.data('draw-tools')?.split(',') ?? _config.drawToolsOverride;
		_config.existingSitesSource = element.data('existing-sites');
		_config.existingSitesCallback = element.data('existing-sites-callback');
		_config.featureDrawnCallback = element.data('feature-drawn-callback');
		_config.showOpusAttribution = element.data('show-attribution') ?? true;
		_config.focusFeaturesOnLoad = element.data('focus-features') ?? _config.focusFeaturesOnLoad;
		_config.intersectionChecking = element.data('intersection-check') ?? _config.intersectionChecking;
		_config.existingSitesIntersectionChecking = element.data('es-intersection-check') ?? _config.existingSitesIntersectionChecking;
		_config.defaultScaleLineUnits = element.data('scale-line-units') ?? _config.defaultScaleLineUnits;
		_config.defaultToolMode = getToolModeByName(element.data('default-tool'));
		_config.featureVariationsEnabled = element.data('feature-variations-enabled') ?? _config.featureVariationsEnabled;
		_config.featureVariationsSource = element.data('feature-variations-source');
		_config.localeSource = element.data('locale-source');
		_config.previewOrigin = element.data('preview-origin');
		_config.annotationView = element.data('annotation-view') ?? _config.annotationView;
		_config.screenshotMap = element.data('screenshot-map') ?? _config.screenshotMap;
		_config.screenshotWidth = element.data('screenshot-width') ?? _config.screenshotWidth;
		_config.screenshotHeight = element.data('screenshot-height') ?? _config.screenshotHeight;
		_config.accessStylingEnabled = localStorage.getItem('omv-access') ?? element.data('access') ?? _config.accessStylingEnabled;
		_config.parentWindowCallbacks = element.data('parent-window-callbacks') ?? _config.parentWindowCallbacks;
		_config.parentWindowCallbackId = element.data('parent-window-callback-id') ?? _config.parentWindowCallbackId;
		_config.editDrawings = element.data('edit-drawings') ?? _config.editDrawings;
		_config.deleteEnabled = element.data('delete-enabled') ?? _config.deleteEnabled;
		_config.siteEditor = element.data('site-editor') ?? _config.siteEditor;
		_config.noFocus = element.data('no-focus') ?? _config.noFocus;
		_config.reqBoundary = element.data('request-boundary') ?? _config.reqBoundary;
		_config.initialZoom = element.data('initial-zoom') ?? _config.initialZoom;
		_config.initialCenter = element.data('initial-center') ?? _config.initialCenter;
		_config.mapViewName = element.data('map-view-name') ?? _config.mapViewName;
		
		if(_config.localeSource)
			_config.localeSource = _config.localeSource.split('$').join(element.data('locale'));
		
		if(element.data('locale')) {
			_config.setLang( element.data('locale') );
		}else{
			_config.setLang( 'en-GB' );
		}
		
		_config.localeSource && _config.lang.loadOverrideSource(_config.localeSource);
		
		setAccessStyling(_config.accessStylingEnabled);
		
		// Use a timeout so the loading box shows and the user doesn't think nothing is happening
		setTimeout(() => _events.invoke('map-viewer-ready'), 10);
	}
	
	/**
		* Window resize handler
		* Sets dimensions globally on _dimensions for use in other modules
	*/
	const onWindowResize = () => {
		const newDimensions:any = getWindowDimensions();
		
		setDimensions(newDimensions);
		
		global._dimensions = newDimensions;
	}
	
	/*
		Draw map dependent items
	*/
	const drawMapDependentItems = () => {
		if(olMap == null || !viewerReady) return;
		
		return (
			<>
				<MapContainer olMap={olMap} />
				<OverviewMap olMap={olMap} overviewMap={overviewMap} />
				<Attributions olMap={olMap} />
				<Tools olMap={olMap} />
			</>
		)
	}
	
	if(!initialized) initialize();
	
	return (
		<>
			{drawMapDependentItems()}
			<StyledTooltip place='bottom' />
			<>
				{drawPopups(popupsOpen)}
			</>
		</>
	)
}

export default App;